import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/img/myGuardianLinklogo.png';
import { ADMIN_NAV_GROUPS, isNavItemActive, isGroupActive } from '../config/adminNav';

/** Collapsible dropdown group rendered inside the sidebar */
function NavDropdown({ group, badges, onClose }) {
  const location = useLocation();
  const groupIsActive = isGroupActive(location.pathname, group);
  const groupBadge = group.badgeKey ? badges[group.badgeKey] : null;

  // Auto-open if a child route is currently active
  const [open, setOpen] = useState(groupIsActive);

  // Re-evaluate when pathname changes (e.g. direct URL load)
  useEffect(() => {
    if (groupIsActive) setOpen(true);
  }, [location.pathname, groupIsActive]);

  return (
    <div className={`nav-dropdown${open ? ' nav-dropdown-open' : ''}${groupIsActive ? ' nav-dropdown-active' : ''}`}>
      {/* Trigger row */}
      <button
        className={`nav-dropdown-trigger${groupIsActive ? ' active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <i className={`fas ${group.icon}`} />
        <span className="nav-dropdown-label">{group.group}</span>
        {groupBadge && <span className="nav-badge">{groupBadge}</span>}
        <i className="fas fa-chevron-down nav-dropdown-chevron" />
      </button>

      {/* Sub-items panel */}
      <div className="nav-dropdown-panel">
        {group.items.map((item) => {
          const { icon, label, path, badgeKey, id } = item;
          const badge = badgeKey ? badges[badgeKey] : null;
          const isActive = isNavItemActive(location.pathname, item);

          return (
            <Link
              key={id}
              className={`nav-sub-item${isActive ? ' active' : ''}`}
              to={path}
              onClick={onClose}
            >
              <i className={`fas ${icon}`} />
              {label}
              {badge && <span className="nav-badge">{badge}</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function Sidebar({ isOpen, onClose, badges = {} }) {
  const location = useLocation();
  const adminData = JSON.parse(localStorage.getItem('adminData') || 'null');

  return (
    <>
      <div id="overlay" className={isOpen ? 'show' : ''} onClick={onClose} />
      <aside id="sidebar" className={isOpen ? 'open' : ''}>
        <div className="logo-area" style={{ padding: '0 16px', background: 'rgba(0,0,0,.25)' }}>
          <img src={logo} alt="myGuardianLink" style={{ height: 48, width: 'auto', objectFit: 'contain' }} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 20 }}>
          {ADMIN_NAV_GROUPS.filter((navGroup) => !navGroup.hidden).map((navGroup) => {
            const { group, dropdown, items } = navGroup;

            /* ── Collapsible dropdown group ─────────────── */
            if (dropdown) {
              return (
                <NavDropdown
                  key={group}
                  group={navGroup}
                  badges={badges}
                  onClose={onClose}
                />
              );
            }

            /* ── Regular flat group ─────────────────────── */
            return (
              <React.Fragment key={group}>
                <div className="nav-group-label">{group}</div>
                {items.map((item) => {
                  const { icon, label, path, badgeKey, id } = item;
                  const badge = badgeKey ? badges[badgeKey] : null;
                  const isActive = isNavItemActive(location.pathname, item);

                  return (
                    <Link
                      key={id}
                      className={`nav-item${isActive ? ' active' : ''}`}
                      to={path}
                      onClick={onClose}
                    >
                      <i className={`fas ${icon}`} />
                      {label}
                      {badge && <span className="nav-badge">{badge}</span>}
                    </Link>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
          <div className="nav-item" style={{ cursor: 'default' }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'linear-gradient(135deg,#ec7426,#D9A441)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              {adminData?.firstName?.charAt(0)}
              {adminData?.lastName?.charAt(0)}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
                {adminData?.firstName} {adminData?.lastName}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)' }}>{adminData?.email}</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
